"use client";

import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from "react";

import { CheckIcon } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

/* ── Size config (Figma spec) ──
 * medium: 24px container, 20px visual box, border-2
 * small:  20px container, 16px visual box, border-1
 */
const SIZE_CONFIG = {
  medium: {
    container: "w-6 h-6",
    box: "w-5 h-5 border-2 rounded-[1px]",
    checkSize: "w-4 h-4",
    dashSize: "w-3 h-0.5",
    numberText: "text-[15px] leading-[1.47] tracking-[-0.075px]",
    labelText: "text-[15px] leading-[140%] font-[600]",
  },
  small: {
    container: "w-5 h-5",
    box: "w-4 h-4 border rounded-[1px]",
    checkSize: "w-3.5 h-3.5",
    dashSize: "w-2 h-0.5",
    numberText: "text-[13px] leading-[1.46]",
    labelText: "text-[13px] leading-[140%] font-[600]",
  },
} as const;

const COLORS = {
  primary: "var(--color-primary-primary700)",
  secondary: "var(--color-gray-gray700)",
} as const;

type CheckboxSize = keyof typeof SIZE_CONFIG;
type CheckboxColor = keyof typeof COLORS;

export interface CheckboxProps
  extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  size?: CheckboxSize;
  color?: CheckboxColor;
  number?: number;
}

const Checkbox = forwardRef<ComponentRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  (
    { className, size = "medium", color = "primary", number, children, ...props },
    ref
  ) => {
    const s = SIZE_CONFIG[size];
    const checkedColor = COLORS[color];
    const isIndeterminate = props.checked === "indeterminate";

    return (
      <label
        className={cn(
          "inline-flex items-center gap-2 cursor-pointer",
          props.disabled && "cursor-default opacity-50",
          className
        )}
      >
        <CheckboxPrimitive.Root
          ref={ref}
          className={cn(
            "group peer shrink-0 relative flex items-center justify-center",
            s.container,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700/50"
          )}
          {...props}
        >
          {/* Visual box */}
          <span
            className={cn(
              "flex items-center justify-center",
              s.box,
              "border-gray-400 bg-white",
              "group-data-[state=checked]:border-transparent",
              "group-data-[state=indeterminate]:border-transparent"
            )}
            aria-hidden
          />
          {/* Filled background is handled via group-data-[state] + inline style */}

          {/* Checked background overlay */}
          <span
            className={cn(
              "absolute flex items-center justify-center rounded-[1px]",
              "opacity-0",
              "group-data-[state=checked]:opacity-100",
              "group-data-[state=indeterminate]:opacity-100",
              "transition-opacity duration-150",
              s.box,
              "border-transparent"
            )}
            style={{ backgroundColor: checkedColor }}
            aria-hidden
          />

          {/* Indicator */}
          <CheckboxPrimitive.Indicator
            className={cn("absolute z-10 flex items-center justify-center", s.container)}
          >
            {number !== undefined ? (
              <span className={cn("font-[500] text-white", s.numberText)}>{number}</span>
            ) : isIndeterminate ? (
              <span className={cn("block bg-white rounded-sm", s.dashSize)} />
            ) : (
              <CheckIcon className={cn(s.checkSize, "text-white")} strokeWidth={3} />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {children && (
          <span className={cn("text-gray-900 select-none", s.labelText)}>
            {children}
          </span>
        )}
      </label>
    );
  }
);
Checkbox.displayName = "Checkbox";

export { Checkbox };
export type { CheckboxColor, CheckboxSize };
